/**
 * GTM / GA4 dataLayer helper.
 *
 * Единая точка push'а событий в dataLayer - чтобы формат не расходился
 * между Vue-компонентами и инлайновыми скриптами volt.
 *
 * Контейнер GTM подключается в header.volt (window.dataLayer уже существует).
 *
 * Правила таксономии (см. GTM_EVENTS):
 *   - Стандартные GA4 ecommerce (view_item_list / view_item / begin_checkout /
 *     purchase) - где есть аналог, чтобы из коробки работали отчёты Monetization.
 *   - purchase шлётся ТОЛЬКО за реальные деньги (Stripe $). Разблокировка за
 *     кредиты -> credits_unlock_completed (не засоряет revenue нулём).
 *   - Подписка Pass -> subscribe (отдельная конверсия, вне ecommerce-revenue).
 *   - Кастом - только под Pass-gate / кредиты / upgrade.
 */

/**
 * Реестр событий = единое отражение карты событий по стадиям.
 *
 * Правило: по умолчанию все события пушим РУКАМИ из приложения.
 * Исключения в реестр НЕ вносим (их не пушим из кода):
 *   - auto  - GA4/GTM шлёт сам (page_view и пр.);
 *   - param - не отдельный event, а параметр другого события (entry_point и пр.).
 */
export const GTM_EVENTS = {

    // ── ① Вход ──────────────────────────────────────────────
    // page_view  - auto  (GA4 enhanced measurement) -> не пушим
    // entry_point - param на page_view              -> не event
    SIGN_IN_REQUESTED : 'sign_in_requested',   // запрос magic-link / кода
    SIGN_UP           : 'sign_up',             // успешная регистрация (key event)
    LOGIN             : 'login',               // успешный вход

    // ── Ecommerce-воронка (стандартные GA4) ─────────────────
    VIEW_ITEM_LIST   : 'view_item_list',
    VIEW_ITEM        : 'view_item',
    SELECT_ITEM      : 'select_item',
    BEGIN_CHECKOUT   : 'begin_checkout',
    ADD_PAYMENT_INFO : 'add_payment_info',
    PURCHASE         : 'purchase',

    // ── ② Лендинг ───────────────────────────────────────────
    GENERATE_LEAD    : 'generate_lead',   // захват email на лендинге (key event)

    // ── ③ Каталог · покупка продукта ────────────────────────
    PRODUCT_CTA_VIEWED      : 'product_cta_viewed',          // рендер purchase-карточки (card_state, product_type)
    PASS_PROMO_CLICKED      : 'pass_promo_clicked',          // клик "Unlock with Pass"
    CREDITS_UNLOCK_STARTED  : 'credits_unlock_started',      // клик "Unlock for N credits"
    PASS_UPGRADE_CLICKED    : 'pass_upgrade_clicked',        // клик "Upgrade Pass"
    PASS_GATE_CTA_CLICKED   : 'pass_gate_cta_clicked',       // клик в gate: see_plans | sign_in

    // ── ④ Подписка (Pass) ───────────────────────────────────
    PRICING_VIEWED        : 'pricing_viewed',          // загрузка pricing (source: gate|product|account)
    SUBSCRIPTION_UPGRADED : 'subscription_upgraded',   // апгрейд плана в аккаунте (prorated, key event)

    // ── ⑤ Личный кабинет ────────────────────────────────────
    // page_view (content_group: account) - auto -> не пушим
    ACCOUNT_CTA_CLICKED : 'account_cta_clicked',   // CTA в кабинете (action: browse_catalog|get_tokens)
    TOKEN_TOPUP_STARTED : 'token_topup_started',   // открытие confirm-модалки top-up (package, value)

    // ── Кастомные конверсии / воронка ───────────────────────
    SUBSCRIBE                : 'subscribe',                  // план Pass (вне ecommerce-revenue)
    CREDITS_UNLOCK_COMPLETED : 'credits_unlock_completed',   // разблокировка за кредиты (value 0, key event)
    PASS_GATE_VIEWED         : 'pass_gate_viewed',           // ③ гостевой gate (product_id - friction-метрика)
    PASS_GATE_BLOCKED        : 'pass_gate_blocked',
};

/**
 * Пуш события в dataLayer.
 *
 * Для ecommerce-событий (payload содержит ключ `ecommerce`) перед push'ем
 * шлём `{ ecommerce: null }` - GA4 требует сброс, иначе items[] склеиваются
 * между последовательными событиями.
 *
 * @param {string} event   имя события (используй GTM_EVENTS.*)
 * @param {object} payload данные события; для ecommerce - { ecommerce: {...} }
 */
export function track( event, payload = {} ) {

    if( ! event )
        return;

    window.dataLayer = window.dataLayer || [];

    if( Object.prototype.hasOwnProperty.call( payload, 'ecommerce' ) )
        window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({ event, ...payload });
}

/**
 * Хелпер для ecommerce-событий.
 *
 * ВАЖНО (WP2): в `ecommerce` кладём ТОЛЬКО стандартные GA4-поля
 * (currency, value, items, transaction_id, item_list_*). Кастомные
 * (не-ecommerce) параметры - `payment_type`, `plan`, `plan_name`,
 * `billing_period`, `source`, `landing_slug` и пр. - передаём ТРЕТЬИМ
 * аргументом, они уходят на ВЕРХНИЙ уровень события (иначе top-level DLV
 * их не подхватят - см. F-09/F-14/F-16).
 *
 * @param {string} event  имя ecommerce-события
 * @param {object} data   стандартные ecommerce-поля -> { ecommerce: data }
 * @param {object} params кастомные поля -> верхний уровень события
 */
export function trackEcommerce( event, data = {}, params = {} ) {
    track( event, { ...params, ecommerce: data } );
}

/**
 * Единый билдер ecommerce-айтема (WP1) - канон shape для ВСЕХ воронок,
 * чтобы GA4 сшивал товар по стадиям (view_item -> begin_checkout -> purchase).
 *
 * Правила:
 *   - item_id  - ВСЕГДА строка (slug), не числовой DB id (стабильно сшивается);
 *   - item_name - title (человекочитаемое имя), не slug;
 *   - item_category - ТОЛЬКО дискриминатор типа: product | tokens | subscription | bundle
 *     (не коллекция, не product_type, не per-plan - см. F-07/F-13/F-19);
 *   - price/discount - числа в $ (не центы). price - полная цена, discount - покрыто
 *     кредитами (гибрид F-05), так что price - discount = вклад в value.
 *
 * @param {object} p { id, name, category, price, quantity?, discount?, index? }
 * @returns {object} ecommerce item
 */
export function gtmItem( p = {} ) {

    const item = {
        item_id       : p.id != null ? String( p.id ) : '',
        item_name     : p.name || '',
        item_category : p.category || 'product',
        price         : p.price != null ? +( +p.price ).toFixed( 2 ) : 0,
        quantity      : p.quantity != null ? p.quantity : 1,
    };

    if( p.discount != null )
        item.discount = +( +p.discount ).toFixed( 2 );

    if( p.index != null )
        item.index = p.index;

    return item;
}

/**
 * Единый билдер кастомных product-параметров (top-level, 3-й аргумент trackEcommerce
 * или payload track). Централизует тип: product_id ВСЕГДА строка (F-03), чтобы не
 * дрейфил по местам вызова (view_item / product_cta_viewed в BuyBox/PassBuyBox).
 *
 * @param {object} p { id, type?, ...extra }  extra - доп. поля (напр. card_state)
 * @returns {object} { product_id, product_type?, ...extra }
 */
export function productParams( p = {} ) {

    const { id, type, ...extra } = p;

    const out = { ...extra };

    if( id != null )
        out.product_id = String( id );

    if( type )
        out.product_type = type;

    return out;
}
